import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  FALLBACK_LOCALE,
  LOCALE_COOKIE,
  REGION_DETAILS_LOCAL_STORAGE_KEY,
  TAbsoluteLocale,
  TCountry,
} from '@src/config/i18n.config'
import setCookie from '@src/quince-site-common/src/utils/setCookie'
import { setInLocalStorageWithExpiry } from '@src/quince-site-common/src/utils/setInLocalStorageWithExpiry'
import { noop } from '@src/utils/functions/utils'
import { parseLocale, TRegionDetail, updateCommunicationPreference } from '@src/utils/i18n.utils'

import withI18nGate from './with-i18n-gate.hoc'

export const LocaleContext = createContext<{
  locale: TAbsoluteLocale
  regionDetails: TRegionDetail
  isLocaleModalOpen: boolean
  setIsLocaleModalOpen: (isLocaleModalOpen: boolean) => void
  setLocale: (
    locale: TAbsoluteLocale,
    clientSide?: boolean,
    shouldUpdateCommunicationPreference?: boolean,
  ) => Promise<void> | void
  isLocaleConfirmationModalOpen: boolean
  showConfirmationModal: (country: TCountry, callback: () => void) => void
  closeConfirmationModal: (confirm: boolean) => void
  confirmationCountry: TCountry | null
}>({
  locale: FALLBACK_LOCALE,
  regionDetails: { regions: {}, regionCodes: [] },
  isLocaleModalOpen: false,
  setIsLocaleModalOpen: noop,
  setLocale: noop,
  isLocaleConfirmationModalOpen: false,
  showConfirmationModal: noop,
  closeConfirmationModal: noop,
  confirmationCountry: null,
})

export const LocaleContextProvider = withI18nGate(
  ({
    children,
    initialLocale,
    regionDetails,
  }: {
    initialLocale: TAbsoluteLocale
    children: React.ReactNode
    regionDetails: TRegionDetail
  }) => {
    const [isLocaleModalOpen, setIsLocaleModalOpen] = useState(false)
    const [confirmationCountry, setConfirmationCountry] = useState<TCountry | null>(null)
    const [isLocaleConfirmationModalOpen, setIsLocaleConfirmationModalOpen] = useState(false)
    const [onConfirm, setOnConfirm] = useState<() => void>(noop)
    const { i18n } = useTranslation()
    const [locale, setLocale] = useState(initialLocale)
    const communicationPreferenceAbortController = useRef<AbortController | null>(null)

    useEffect(() => {
      const { country, language } = parseLocale(locale)
      const { regionId } = regionDetails.regions[country] ?? { regionId: null }
      setInLocalStorageWithExpiry(
        REGION_DETAILS_LOCAL_STORAGE_KEY,
        JSON.stringify({
          regionId,
          locale,
          country,
          language,
        }),
      )
    }, [initialLocale, regionDetails, locale])

    const providerValue = useMemo(() => {
      const handleSetLocale = async (
        locale: TAbsoluteLocale,
        clientSide = false,
        shouldUpdateCommunicationPreference = true,
      ) => {
        const { country, language } = parseLocale(locale)
        const { regionId } = regionDetails.regions[country] ?? { regionId: null }
        setCookie(LOCALE_COOKIE, locale, undefined, undefined, true)
        setInLocalStorageWithExpiry(
          REGION_DETAILS_LOCAL_STORAGE_KEY,
          JSON.stringify({
            regionId,
            locale,
            country,
            language,
          }),
        )
        await i18n.changeLanguage(locale)
        setLocale(locale)

        if (shouldUpdateCommunicationPreference) {
          communicationPreferenceAbortController.current?.abort()
          communicationPreferenceAbortController.current = new AbortController()
          await updateCommunicationPreference({
            locale,
            regionDetails,
            abortController: communicationPreferenceAbortController.current,
          })
        }
        if (!clientSide) window.location.reload()
      }
      return {
        locale,
        isLocaleModalOpen,
        setIsLocaleModalOpen,
        setLocale: handleSetLocale,
        regionDetails,
        isLocaleConfirmationModalOpen,
        confirmationCountry,
        showConfirmationModal: (country: TCountry, callback: () => void) => {
          setIsLocaleConfirmationModalOpen(true)
          setOnConfirm(() => callback)
          setConfirmationCountry(country)
        },
        closeConfirmationModal: (confirm: boolean) => {
          if (confirm) onConfirm()
          setIsLocaleConfirmationModalOpen(false)
          setOnConfirm(noop)
          setConfirmationCountry(null)
        },
      }
    }, [locale, isLocaleModalOpen, regionDetails, isLocaleConfirmationModalOpen, confirmationCountry, i18n, onConfirm])

    return <LocaleContext.Provider value={providerValue}>{children}</LocaleContext.Provider>
  },
  true,
)

export const useLocaleContext = () => {
  return useContext(LocaleContext)
}
